import { apiClient } from "./apiClient";
import { reverseGeocode } from "./geocodingService";
import { getCurrentPosition } from "./geolocationService";
import { APP_LOCATION_CITY_MAPPING } from "@constants/appLocationMapping";
import { CITY_ALIASES } from "@constants/cities";
import { getCities } from "./cityService";
import { City } from "@components/organisms/CitySelectionModal/City.types";

export interface GarageSlotsDataResult {
  success: boolean;
  data: any | null;
  detectedCityName: string | null;
  resolvedCity: City | null;
}

const normalizeCityValue = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getMappedCityKey = (cityName: string): string => {
  const normalizedCityName = normalizeCityValue(cityName);
  return (
    APP_LOCATION_CITY_MAPPING[normalizedCityName] ||
    CITY_ALIASES[normalizedCityName] ||
    normalizedCityName
  );
};

const resolveSellableCityFromLocation = async (
  cityName: string | null | undefined
): Promise<City | null> => {
  const trimmedCityName = cityName?.trim();
  if (!trimmedCityName) {
    return null;
  }

  try {
    const cityKey = getMappedCityKey(trimmedCityName);
    const cities = await getCities();

    return (
      cities.find((city) => {
        const citySlug = city.city_slug ? normalizeCityValue(city.city_slug) : "";
        const normalizedName = city.name ? normalizeCityValue(city.name) : "";
        return citySlug === cityKey || normalizedName === cityKey;
      }) || null
    );
  } catch (error: any) {
    console.error("Error while resolving current location city:", error?.message);
    return null;
  }
};

export const getGarageSlotsData = async (): Promise<GarageSlotsDataResult> => {

  const getValidIndianPincode = (pincode: any) => {
    if (typeof pincode === "string" && /^[1-9][0-9]{5}$/.test(pincode.trim())) {
      return Number(pincode);
    }
    return null;
  };

  let detectedCityName: string | null = null;
  let resolvedCity: City | null = null;

  try {
    const { lat, lon } = await getCurrentPosition();
    const geoData = await reverseGeocode(lat, lon);
    detectedCityName = geoData?.city || null;
    resolvedCity = await resolveSellableCityFromLocation(detectedCityName);
    const pincode = getValidIndianPincode(geoData?.pincode);

    if (!pincode) {
      console.error("Error While fetching garage service pincode");
      return {
        success: false,
        data: null,
        detectedCityName,
        resolvedCity,
      };
    }

    const response = await apiClient.get(
      `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/${process.env.NEXT_PUBLIC_BASE_TERM}api/garage/fetchslots?pincode=${pincode}&lat=${lat}&lon=${lon}`,
    );

    return {
      success: true,
      data: response,
      detectedCityName,
      resolvedCity,
    };
  } catch (error: any) {
    console.error("Error While fetching garage service Slots data ", error?.message);
    return {
      success: false,
      data: null,
      detectedCityName,
      resolvedCity,
    };
  }
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = 10000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const getGarageServiceData = async (userId: string): Promise<any | null> => {
  if (!userId) {
    return { success: false, data: null };
  }

  try {
    const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL ?? ""}/api/garage?userId=${userId}`;

    const response = await fetchWithTimeout(
      apiUrl,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      5000
    );

    const data = await response.json();

    if (
      !data ||
      (typeof data === "object" && Object.keys(data).length === 0) ||
      data.success === false
    ) {
      return { success: false, data: null };
    }

    const mainKey = Object.keys(data)[0];
    const serviceData = data[mainKey];

    if (
      serviceData?.status_code >= 400 ||
      typeof serviceData?.response?.error === "string"
    ) {
      return { success: false, data: null };
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.warn("Garage service data fetch timed out after 10 seconds");
      return { success: false, data: null, timeout: true };
    }
    console.error("Error while fetching garage service data:", error?.message);
    return {
      success: false,
      data: null,
    };
  }
};
